import React, { useState, useEffect, Suspense, useCallback } from "react";
import { IconButton } from "@mui/material";
import PrivateMenu from "./private-menus";
import PublicMenu from "./public-menu";
import programmingContentService from "app/services/programming-content-service";
import { useNavigate, useLocation } from "react-router-dom";
import { DrawerHeader } from "@jumbo/components/JumboLayout/style";
import JumboScrollbar from "@jumbo/components/JumboScrollbar";
import JumboVerticalNavbar from "@jumbo/components/JumboVerticalNavbar/JumboVerticalNavbar";
import useJumboLayoutSidebar from "@jumbo/hooks/useJumboLayoutSidebar";
import useJumboSidebarTheme from "@jumbo/hooks/useJumboSidebarTheme";
import { SIDEBAR_STYLES, SIDEBAR_VIEWS } from "@jumbo/utils/constants/layout";
import Logo from "../../../../shared/Logo";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import Zoom from "@mui/material/Zoom";
import Div from "@jumbo/shared/Div";
import SidebarSkeleton from "./SidebarSkeleton";
import useAdmin from "app/routes/middleware/Admin/useAdmin";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { useTranslation } from "react-i18next";
import useMetadata from "app/hooks/useMetadata";
import { ROUTES } from "app/utils/constants/routeConstants";
import { ASSET_PROGRAMMING_LANGUAGES } from "app/utils/constants/paths";
import { PROGRAMMING_CONTENT_PATH_REGEX } from "app/utils/constants/regexConstants";

const Sidebar = () => {
  const { t } = useTranslation();
  const isAdmin = useAdmin();
  const { authToken } = useJumboAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState({});
  const [contents, setContents] = useState({});
  const [loading, setLoading] = useState({});
  const { metadata } = useMetadata();

  const languages = (metadata?.programmingLanguages || []).map(
    ({ key, value }) => ({
      name: value,
      param: key,
      icon: (
        <img
          src={`${ASSET_PROGRAMMING_LANGUAGES}/${value.toLowerCase()}.png`}
          alt={value}
          style={{ width: 24, height: 24 }}
        />
      ),
    })
  );

  // Handle language content fetching
  const handleLanguageClick = useCallback(async (language, label) => {
    const isOpen = open[label] || false;

    // Toggle collapsible state
    setOpen((prev) => ({
      ...prev,
      [label]: !isOpen,
    }));

    // Fetch content if not already fetched
    if (!isOpen && !contents[language.name]) {
      setLoading((prev) => ({ ...prev, [language.name]: true }));
      try {
        const data = await programmingContentService.getProgrammingContentsMenu(
          language.param
        );
        setContents((prev) => ({ ...prev, [language.name]: data }));
      } catch (error) {
        console.error(`Failed to fetch ${language.name} contents:`, error);
        setContents((prev) => ({ ...prev, [language.name]: [] }));
      } finally {
        setLoading((prev) => ({ ...prev, [language.name]: false }));
      }
    }
  }, [open, contents]);

  // Automatically open language section for programming content page
  useEffect(() => {
    const isLanguagePage = location.pathname.match(
      PROGRAMMING_CONTENT_PATH_REGEX
    );
    if (isLanguagePage) {
      const selectedLanguage = isLanguagePage[1];
      const language = languages.find(
        (lang) => lang.name.toLowerCase() === selectedLanguage.toLowerCase()
      );
      if (language) {
        const label = `sidebar.menuItem.${language.name.toLowerCase()}`;
        if (!open[label]) {
          setOpen((prev) => ({
            ...prev,
            [label]: true,
          }));
          if (!contents[language.name]) {
            handleLanguageClick(language, label);
          }
        }
      }
    }
  }, [location.pathname, languages, open, contents, handleLanguageClick]);

  const handleMenuItemClick = (uri) => {
    navigate(uri);
  };

  // Base menu items (PrivateMenu or PublicMenu)
  let menuItems = [];
  if (isAdmin) {
    menuItems = PrivateMenu();
  } else {
    menuItems = PublicMenu(authToken);
  }

  // Dynamic language items for programming content
  const isLanguagePage = location.pathname.match(
    PROGRAMMING_CONTENT_PATH_REGEX
  );
  const selectedLanguage = isLanguagePage ? isLanguagePage[1] : null;

  let languageItems = [];
  if (selectedLanguage && authToken) {
    const language = languages.find(
      (lang) => lang.name.toLowerCase() === selectedLanguage.toLowerCase()
    );
    if (language) {
      const label = `sidebar.menuItem.${language.name.toLowerCase()}`;
      languageItems = [
        {
          label: label,
          type: "collapsible",
          icon: language.icon,
          onClick: () => handleLanguageClick(language, label),
          children:
            contents[language.name]?.length > 0
              ? contents[language.name].map((item) => ({
                  uri: `${
                    ROUTES.PROGRAMMING_CONTENT
                  }/${language.name.toLowerCase()}/${item.url}`,
                  label: item.title,
                  type: "nav-item",
                  onClick: () =>
                    handleMenuItemClick(
                      `${
                        ROUTES.PROGRAMMING_CONTENT
                      }/${language.name.toLowerCase()}/${item.url}`
                    ),
                }))
              : loading[language.name]
              ? [
                  {
                    label: t("sidebar.menuItem.loading"),
                    type: "nav-item",
                    disabled: true,
                  },
                ]
              : [
                  {
                    label: t("sidebar.menuItem.noContent", {
                      language: language.name,
                    }),
                    type: "nav-item",
                    disabled: true,
                  },
                ],
        },
      ];
    }
  }

  // Combine menu items
  const combinedMenuItems = [
    ...(authToken && selectedLanguage
      ? [
          {
            label: "sidebar.menu.ProgrammingContent",
            type: "section",
            children: languageItems,
          },
        ]
      : []),
    ...menuItems,
  ];

  return (
    <React.Fragment>
      <SidebarHeader />
      <JumboScrollbar autoHide autoHideDuration={200} autoHideTimeout={500}>
        <Suspense
          fallback={
            <Div
              sx={{
                display: "flex",
                minWidth: 0,
                alignItems: "center",
                alignContent: "center",
                px: 3,
              }}
            >
              <SidebarSkeleton />
            </Div>
          }
        >
          <JumboVerticalNavbar translate items={combinedMenuItems} />
        </Suspense>
      </JumboScrollbar>
    </React.Fragment>
  );
};

const SidebarHeader = () => {
  const { sidebarOptions, setSidebarOptions } = useJumboLayoutSidebar();
  const { sidebarTheme } = useJumboSidebarTheme();

  const isMiniAndClosed = React.useMemo(() => {
    return sidebarOptions?.view === SIDEBAR_VIEWS.MINI && !sidebarOptions?.open;
  }, [sidebarOptions.view, sidebarOptions.open]);

  return (
    <React.Fragment>
      {sidebarOptions?.style !== SIDEBAR_STYLES.CLIPPED_UNDER_HEADER && (
        <DrawerHeader>
          <Logo mini={isMiniAndClosed} mode={sidebarTheme.type} />
          {sidebarOptions?.view !== SIDEBAR_VIEWS.MINI && (
            <Zoom in={sidebarOptions?.open}>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="open drawer"
                sx={{ ml: 0, mr: -1.5 }}
                onClick={() => setSidebarOptions({ open: false })}
              >
                <MenuOpenIcon />
              </IconButton>
            </Zoom>
          )}
        </DrawerHeader>
      )}
    </React.Fragment>
  );
};

export default Sidebar;
